import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getUserAccessibleProjects, getRelatedPeopleForUser } from '../utils/userPermissions';
import { 
  Plus, 
  Target, 
  Network, 
  Users, 
  Briefcase, 
  TrendingUp, 
  Calendar, 
  FolderGit2
} from 'lucide-react';

const WelcomePage = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();

  const [projects, setProjects] = useState([]);
  const [peopleCount, setPeopleCount] = useState(0);

  useEffect(() => {
    const loadData = () => {
      const allProjects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const allPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      
      const userProjects = getUserAccessibleProjects(allProjects, currentUser, isAdmin);
      const userPeople = getRelatedPeopleForUser(allPeople, userProjects, currentUser, isAdmin);

      setProjects(userProjects);
      setPeopleCount(userPeople.length);
    };

    loadData();
    window.addEventListener('storage', loadData);
    return () => window.removeEventListener('storage', loadData);
  }, [currentUser, isAdmin]);

  const activeProjectsCount = projects.filter(p => p.status !== 'Completed').length;
  const completionRate = projects.length > 0 
    ? Math.round((projects.filter(p => p.status === 'Completed').length / projects.length) * 100) 
    : 0;

  const stats = [
    { label: isAdmin ? 'Active Projects' : 'My Active Projects', value: String(activeProjectsCount), icon: Briefcase, color: 'text-blue-400' },
    { label: isAdmin ? 'Total Members' : 'Related Talent', value: String(peopleCount), icon: Users, color: 'text-green-400' },
    { label: 'Skills Mapped', value: isAdmin ? '45' : String(Math.max(projects.length * 3, 4)), icon: Target, color: 'text-purple-400' },
    { label: 'Completion Rate', value: `${completionRate}%`, icon: TrendingUp, color: 'text-orange-400' }
  ];

  const quickActions = isAdmin ? [
    {
      title: 'Create Project',
      description: 'Start a new project and define required skills',
      icon: Plus,
      href: '/projects/new',
      color: 'bg-blue-500/20 text-blue-400'
    },
    {
      title: 'Explore Matching',
      description: 'Find the best people for your projects',
      icon: Target,
      href: '/match',
      color: 'bg-green-500/20 text-green-400'
    },
    {
      title: 'Open Graph',
      description: 'Visualize your skills network and connections',
      icon: Network,
      href: '/graph',
      color: 'bg-purple-500/20 text-purple-400'
    }
  ] : [
    {
      title: 'Explore Projects & Apply',
      description: 'Browse all projects, inspect needed skills, and apply to teams',
      icon: Briefcase,
      href: '/projects',
      color: 'bg-blue-500/20 text-blue-400'
    },
    {
      title: 'My Skills & Profile',
      description: 'Keep your skill inventory updated for high match scores',
      icon: Target,
      href: '/me',
      color: 'bg-purple-500/20 text-purple-400'
    },
    {
      title: 'Create Project',
      description: 'Publish your own project and recruit matching talent',
      icon: Plus,
      href: '/projects/new',
      color: 'bg-green-500/20 text-green-400'
    }
  ];

  const recentProjects = projects.slice(0, 3).map(p => ({
    id: p.id,
    name: p.name,
    status: p.status || 'In Progress',
    progress: p.progress || 35,
    teamSize: p.teamSize || 4,
    deadline: p.endDate || 'Active',
    requiredSkills: Array.isArray(p.requiredSkills) ? p.requiredSkills : []
  }));

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'In Progress': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Planning': return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-semibold';
      case 'On Hold': return isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      default: return isDarkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Hero Section */}
      <div className="text-center py-16">
        <h1 className="text-5xl font-bold mb-4">
          Hello, {currentUser?.name || 'Explorer'} 👋
        </h1>
        <p className={`text-xl transition-colors duration-200 ${
          isDarkMode ? 'text-dark-300' : 'text-light-600'
        } max-w-2xl mx-auto`}>
          {isAdmin 
            ? 'Welcome to SkillMesh Admin. Manage all participant projects and skill clusters across the platform.' 
            : 'Welcome to SkillMesh! Track your published projects and find matching talent tailored to your skill demands.'}
        </p>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {stats.map((stat, index) => (
          <div key={index} className={`card text-center transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}>
            <div className={`w-12 h-12 ${stat.color} bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="text-2xl font-bold mb-2">{stat.value}</div>
            <div className={`transition-colors duration-200 ${
              isDarkMode ? 'text-dark-300' : 'text-light-600'
            }`}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-center mb-8">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.href}
              className={`card text-center hover:scale-105 transition-all duration-200 ${
                isDarkMode ? 'dark' : 'light'
              }`}
            >
              <div className={`w-16 h-16 ${action.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <action.icon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{action.title}</h3>
              <p className={`transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                {action.description}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Projects */}
      <div className="mb-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold">
            {isAdmin ? 'Recent Projects' : 'My Published Projects'}
          </h2>
          <Link
            to="/projects"
            className="text-primary-400 hover:text-primary-300 font-medium"
          >
            View All Projects →
          </Link>
        </div>
        
        {recentProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentProjects.map((project) => (
              <div key={project.id} className={`card transition-all duration-200 ${
                isDarkMode ? 'dark' : 'light'
              }`}>
                {/* Project Header */}
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold">{project.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>

                {/* Project Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <Users className={`h-4 w-4 ${
                        isDarkMode ? 'text-dark-400' : 'text-light-500'
                      }`} />
                      <span>Team Size</span>
                    </div>
                    <span className="font-medium">{project.teamSize} members</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className={`h-4 w-4 ${
                        isDarkMode ? 'text-dark-400' : 'text-light-500'
                      }`} />
                      <span>Target Date</span>
                    </div>
                    <span className="font-medium">{project.deadline}</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className={`transition-colors duration-200 ${
                      isDarkMode ? 'text-dark-300' : 'text-light-600'
                    }`}>
                      Progress
                    </span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className="w-full bg-dark-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project.progress)}`}
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-dark-800">
                  <Link to={`/projects/${project.id}`} className="text-primary-400 hover:text-primary-300 text-sm font-medium">
                    View Project Details →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12 px-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 mb-4">
              <FolderGit2 className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold mb-2">No Published Projects Yet</h3>
            <p className="text-sm text-gray-400 max-w-md mx-auto mb-6">
              You haven't published any projects yet. Publish your project to start matching with team members and needed skills!
            </p>
            <Link to="/projects/new" className="btn-primary inline-flex items-center">
              <Plus className="h-5 w-5 mr-2" />
              Publish Your First Project
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
